import { Injectable, Signal } from '@angular/core'
import { RemoteData, selectWith } from '@ftr/foundation'
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store'
import { memoize } from 'lodash-es'
import { defaultFlags, FeatureFlagKeyOfType, LaunchDarklyFlagValue, WebFeatureFlags } from '../feature-flags'
import { SetFeatureFlagValues } from './feature-flag.actions'
import { FeatureFlagStateModel } from './feature-flag.model'

export function defaultFeatureFlagState(): FeatureFlagStateModel {
  return {
    featureFlags: defaultFlags,
    hasInitializedNonDefaultFlagValues: false,
  }
}

@State<FeatureFlagStateModel>({
  name: 'featureFlagState',
  defaults: defaultFeatureFlagState(),
})
@Injectable()
export class FeatureFlagState {
  @Selector()
  static allFeatureFlags(state: FeatureFlagStateModel): WebFeatureFlags {
    return state.featureFlags
  }

  static featureFlag = memoize(
    <T extends LaunchDarklyFlagValue = boolean>(
      key: FeatureFlagKeyOfType<T>,
    ): ((featureFlags: ReturnType<typeof FeatureFlagState.allFeatureFlags>) => T) => {
      return createSelector(
        [FeatureFlagState.allFeatureFlags],
        featureFlags => featureFlags[key as keyof WebFeatureFlags] as T,
      )
    },
  )

  static resolvedFeatureFlagValue = memoize(
    <T extends LaunchDarklyFlagValue = boolean>(
      key: FeatureFlagKeyOfType<T>,
    ): ((state: FeatureFlagStateModel) => RemoteData<T>) => {
      return createSelector([FeatureFlagState], state => {
        if (!state.hasInitializedNonDefaultFlagValues) {
          return RemoteData.loading()
        }
        return RemoteData.success(state.featureFlags[key as keyof WebFeatureFlags] as T)
      })
    },
  )

  @Action(SetFeatureFlagValues)
  setFeatureFlagValues(
    { patchState, getState }: StateContext<FeatureFlagStateModel>,
    { featureFlags, hasInitializedNonDefaultFlagValues }: SetFeatureFlagValues,
  ): void {
    const { featureFlags: currentFlags } = getState()

    patchState({
      featureFlags: Object.fromEntries(
        (Object.keys(defaultFlags) as (keyof WebFeatureFlags)[]).map(key => [
          key,
          featureFlags[key] ?? currentFlags[key],
        ]),
      ) as unknown as WebFeatureFlags,
      hasInitializedNonDefaultFlagValues,
    })
  }
}

/**
 * Wrapper over the feature flag select which provide a signal for a specific feature flag.
 * @see import('../testing/jest/mock-feature-flag').mockFeatureFlag
 */
export function featureFlag<K extends keyof WebFeatureFlags>(key: K): Signal<WebFeatureFlags[K]> {
  return selectWith(() => FeatureFlagState.featureFlag(key as FeatureFlagKeyOfType<any>))
}
